import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { stationsApi } from '../../lib/stationsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, optionsLibelles } from '../../lib/libelles';
import { formatDate, formatDateHeure, formatMontant } from '../../lib/formats';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { StationPicker, useDroitsStation, useStations } from '../../components/stations/StationPicker';
import ScheduledPriceChanges from '../../components/stations/ScheduledPriceChanges';

/**
 * Prix des stations (lot 029) : prix en vigueur, publication d'un prix avec sa
 * date et son heure d'effet, plafonds nationaux, historique. Le serveur refuse
 * un prix au-dessus du plafond en vigueur ; l'écran le signale avant l'envoi.
 */

const PAYS_PLAFOND = ['ML', 'CI', 'SN', 'BF', 'NE', 'GN', 'MR', 'TG', 'BJ'];

/** Valeur d'un champ « date et heure » locale pour maintenant. */
const maintenantLocal = () => {
  const date = new Date();
  date.setSeconds(0, 0);
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 16);
};

/** Champ « date et heure » locale → ISO avec fuseau (UTC). */
const versIso = (local) => {
  if (!local) return undefined;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const Champ = ({ id, label, aide, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const Panneau = ({ titre, aide, droite, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="overflow-x-auto px-1 pb-3">{children}</div>
  </section>
);

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0';

// --- Publier un prix ----------------------------------------------------------------------

const PrixDialog = ({ open, onOpenChange, stationId, lignes, produitInitial, onSaved }) => {
  const [produitId, setProduitId] = useState('');
  const [prix, setPrix] = useState('');
  const [effet, setEffet] = useState(maintenantLocal());
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (!open) return;
    setProduitId(produitInitial || lignes.find((ligne) => ligne.in_station)?.product_id || lignes[0]?.product_id || '');
    setPrix('');
    setEffet(maintenantLocal());
    setErreur('');
  }, [open, produitInitial, lignes]);

  const options = useMemo(
    () =>
      lignes.map((ligne) => ({
        value: ligne.product_id,
        label: ligne.product_name,
        description: ligne.price ? `Prix actuel : ${formatMontant(ligne.price.unit_price)}` : 'Aucun prix',
      })),
    [lignes],
  );
  const ligne = lignes.find((item) => item.product_id === produitId);
  const plafond = ligne?.ceiling?.ceiling_price;
  const auDessus = plafond !== undefined && plafond !== null && Number(prix) > Number(plafond);

  const publier = async (event) => {
    event.preventDefault();
    setErreur('');
    setEnCours(true);
    try {
      // Heure par défaut devenue passée pendant la saisie : « maintenant » (le serveur fait de même).
      const iso = versIso(effet);
      const effectiveAt = iso && new Date(iso).getTime() > Date.now() ? iso : undefined;
      const publie = await stationsApi.prices.publish(stationId, {
        productId: produitId,
        unitPrice: Number(prix),
        effectiveAt,
      });
      setEffet(maintenantLocal());
      toast.success(publie?.requires_price_change_reading ? 'Prix publié : relevez les index des pistolets concernés' : 'Prix publié');
      onSaved(publie);
      onOpenChange(false);
    } catch (probleme) {
      const message = messageErreur(probleme, 'Impossible de publier le prix');
      setErreur(message);
      toast.error(message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Publier un prix
            <Aide titre="Date et heure d’effet">
              Le prix s’applique à partir de la date et de l’heure choisies. Un poste ouvert au moment du changement
              demande le relevé des index des pistolets du produit.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={publier} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Champ id="prix-produit" label="Produit">
              <SearchableSelect id="prix-produit" value={produitId} onValueChange={setProduitId} options={options} aria-label="Produit" placeholder="Choisir le produit" />
            </Champ>
          </div>
          <Champ id="prix-montant" label="Prix du litre (FCFA)">
            <Input
              id="prix-montant"
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={prix}
              onChange={(event) => setPrix(event.target.value)}
              required
              aria-invalid={auDessus}
              className="text-right tabular-nums"
            />
          </Champ>
          <Champ id="prix-effet" label="À partir du">
            <Input id="prix-effet" type="datetime-local" value={effet} onChange={(event) => setEffet(event.target.value)} required />
          </Champ>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            {plafond !== undefined && plafond !== null && (
              <Badge variant={auDessus ? 'destructive' : 'muted'}>
                Plafond {formatMontant(plafond)}
                {ligne?.ceiling?.effective_at ? ` depuis le ${formatDate(ligne.ceiling.effective_at)}` : ''}
              </Badge>
            )}
            {auDessus && <Badge variant="destructive">Au-dessus du plafond</Badge>}
          </div>
          {erreur && (
            <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
              {erreur}
            </p>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !produitId || !prix || auDessus}>
              Publier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Plafonds -------------------------------------------------------------------------------

const PlafondDialog = ({ open, onOpenChange, produits, onSaved }) => {
  const [form, setForm] = useState({ country: 'ML', product_code: '', ceiling_price: '', effective_at: maintenantLocal(), reference: '' });
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ country: 'ML', product_code: produits[0]?.product_code || '', ceiling_price: '', effective_at: maintenantLocal(), reference: '' });
    }
  }, [open, produits]);
  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const options = useMemo(
    () => produits.map((produit) => ({ value: produit.product_code, label: produit.product_name })),
    [produits],
  );

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      const cree = await stationsApi.ceilings.create({
        country: form.country,
        product_code: form.product_code,
        ceiling_price: Number(form.ceiling_price),
        effective_at: versIso(form.effective_at),
        reference: form.reference.trim() || undefined,
      });
      const audessus = cree?.stations_above || [];
      if (audessus.length) {
        toast.warning(
          `Plafond enregistré : ${audessus.length} station${audessus.length > 1 ? 's' : ''} au-dessus (${audessus
            .map((ligne) => ligne.station_name)
            .join(', ')})`,
        );
      } else {
        toast.success('Plafond enregistré');
      }
      onSaved(cree);
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’enregistrer le plafond'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nouveau plafond</DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="plafond-pays" label="Pays">
            <SearchableSelect id="plafond-pays" value={form.country} onValueChange={changer('country')} options={optionsLibelles('pays', PAYS_PLAFOND)} aria-label="Pays" />
          </Champ>
          <Champ id="plafond-produit" label="Produit">
            <SearchableSelect id="plafond-produit" value={form.product_code} onValueChange={changer('product_code')} options={options} aria-label="Produit" placeholder="Choisir le produit" />
          </Champ>
          <Champ id="plafond-prix" label="Plafond du litre (FCFA)">
            <Input id="plafond-prix" type="number" inputMode="numeric" min="1" step="1" value={form.ceiling_price} onChange={changer('ceiling_price')} required className="text-right tabular-nums" />
          </Champ>
          <Champ id="plafond-effet" label="À partir du">
            <Input id="plafond-effet" type="datetime-local" value={form.effective_at} onChange={changer('effective_at')} required />
          </Champ>
          <div className="sm:col-span-2">
            <Champ id="plafond-reference" label="Référence du texte officiel">
              <Input id="plafond-reference" value={form.reference} onChange={changer('reference')} maxLength={120} />
            </Champ>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !form.product_code || !form.ceiling_price}>
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Page ------------------------------------------------------------------------------------

const StationPricesPage = () => {
  const droits = useDroitsStation();
  const { stations } = useStations();
  const [stationId, setStationId] = useState('');
  const station = stations.find((ligne) => ligne.id === stationId) || null;

  const [actifs, setActifs] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [plafonds, setPlafonds] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [dialogue, setDialogue] = useState({ type: null, produitId: null });

  const charger = useCallback(async (id) => {
    if (!id) return;
    setChargement(true);
    const [lesActifs, lHistorique, lesPlafonds] = await Promise.allSettled([
      stationsApi.prices.active(id),
      stationsApi.prices.history(id),
      stationsApi.ceilings.list(),
    ]);
    if (lesActifs.status === 'rejected') toast.error(messageErreur(lesActifs.reason, 'Impossible de charger les prix'));
    setActifs(lesActifs.status === 'fulfilled' ? lesActifs.value : null);
    setHistorique(lHistorique.status === 'fulfilled' ? lHistorique.value || [] : []);
    setPlafonds(lesPlafonds.status === 'fulfilled' ? lesPlafonds.value || [] : []);
    setChargement(false);
  }, []);

  useEffect(() => {
    charger(stationId);
  }, [stationId, charger]);

  const lignes = useMemo(() => actifs?.items || [], [actifs]);
  // Les produits de la station d'abord (ceux d'une cuve), puis les autres produits qui ont un prix.
  const lignesVisibles = lignes.filter((ligne) => ligne.in_station || ligne.price);
  const sansPrix = lignes.filter((ligne) => ligne.in_station && !ligne.price).length;
  const auDessus = lignes.filter((ligne) => ligne.above_ceiling).length;
  const programmes = lignes.filter((ligne) => ligne.next_price).length;
  const nomsProduits = useMemo(
    () => Object.fromEntries(lignes.map((ligne) => [ligne.product_code, ligne.product_name])),
    [lignes],
  );
  const peutPublier = droits.peutEcrire('prices') && Boolean(station);

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Prix"
        description="Prix de vente par produit, date et heure d’effet, plafonds nationaux."
        meta={station ? nomLisible(station.name, station.code) : undefined}
        alertes={[
          sansPrix > 0 && {
            niveau: 'critique',
            texte: `${sansPrix} produit${sansPrix > 1 ? 's' : ''} de la station sans prix : aucun poste ne s’ouvre sur ce produit`,
            court: 'Sans prix',
            nombre: sansPrix,
          },
          auDessus > 0 && {
            niveau: 'critique',
            texte: `${auDessus} prix au-dessus du plafond en vigueur`,
            court: 'Au-dessus du plafond',
            nombre: auDessus,
          },
          programmes > 0 && {
            niveau: 'info',
            texte: `${programmes} changement${programmes > 1 ? 's' : ''} de prix programmé${programmes > 1 ? 's' : ''}`,
            court: 'Prix programmé',
            nombre: programmes,
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={setStationId} />
            {peutPublier && (
              <Button type="button" onClick={() => setDialogue({ type: 'prix', produitId: null })}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Publier un prix
              </Button>
            )}
          </>
        }
      />

      {!station ? (
        <EmptyState variant="empty" title="Choisissez une station" className="border-0" />
      ) : chargement && !actifs ? (
        <LoadingState shape="table" />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <div className="grid content-start gap-4">
            <Panneau titre="Prix en vigueur" droite={<span className="text-xs text-muted-foreground">FCFA le litre</span>}>
              {lignesVisibles.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Aucun produit</p>
              ) : (
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Produit</th>
                      <th className="text-right">Prix</th>
                      <th className="text-left">Depuis</th>
                      <th className="text-right">Prochain prix</th>
                      <th className="text-right">Plafond</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {lignesVisibles.map((ligne) => (
                      <tr key={ligne.product_id} className={ligne.above_ceiling ? 'bg-destructive/5' : undefined}>
                        <td className="font-medium">
                          {ligne.product_name}
                          {!ligne.in_station && <span className="text-xs text-muted-foreground"> · sans cuve</span>}
                        </td>
                        <td className="text-right font-semibold tabular-nums">
                          {ligne.price ? formatMontant(ligne.price.unit_price) : <Badge variant="destructive">Sans prix</Badge>}
                        </td>
                        <td className="whitespace-nowrap">{ligne.price ? formatDateHeure(ligne.price.effective_at) : '—'}</td>
                        <td className="whitespace-nowrap text-right tabular-nums">
                          {ligne.next_price ? (
                            <>
                              {formatMontant(ligne.next_price.unit_price)}
                              <span className="block text-xs text-muted-foreground">{formatDateHeure(ligne.next_price.effective_at)}</span>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={`text-right tabular-nums ${ligne.above_ceiling ? 'font-semibold text-destructive' : ''}`}>
                          {ligne.ceiling ? formatMontant(ligne.ceiling.ceiling_price) : '—'}
                        </td>
                        <td className="text-right">
                          {peutPublier && (
                            <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ type: 'prix', produitId: ligne.product_id })}>
                              Changer
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>

            <ScheduledPriceChanges
              stationId={station.id}
              produits={lignes}
              stations={stations}
              peutEcrire={peutPublier}
              onChanged={() => charger(station.id)}
            />

            <Panneau titre="Historique">
              {historique.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Aucun prix publié</p>
              ) : (
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">À partir du</th>
                      <th className="text-left">Produit</th>
                      <th className="text-right">Prix</th>
                      <th className="text-left">Publié le</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historique.map((ligne) => (
                      <tr key={ligne.id}>
                        <td className="whitespace-nowrap">{formatDateHeure(ligne.effective_at || ligne.created_at)}</td>
                        <td>{ligne.product_name || libelle('fuelProduct', ligne.product_code, 'Produit à préciser')}</td>
                        <td className="text-right tabular-nums">{formatMontant(ligne.unit_price)}</td>
                        <td className="whitespace-nowrap text-muted-foreground">{formatDateHeure(ligne.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
          </div>

          <Panneau
            titre="Plafonds nationaux"
            aide={
              <Aide titre="Plafonds nationaux">
                Le prix plafond fixé par l’État pour chaque produit. Une station ne publie pas de prix au-dessus du
                plafond en vigueur dans son pays. Seul l’administrateur saisit un plafond.
              </Aide>
            }
            droite={
              droits.estAdministrateur && (
                <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ type: 'plafond', produitId: null })}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Plafond
                </Button>
              )
            }
          >
            {plafonds.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Aucun plafond</p>
            ) : (
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">À partir du</th>
                    <th className="text-left">Pays</th>
                    <th className="text-left">Produit</th>
                    <th className="text-right">Plafond</th>
                  </tr>
                </thead>
                <tbody>
                  {plafonds.map((plafond) => (
                    <tr key={plafond.id}>
                      <td className="whitespace-nowrap">
                        {formatDate(plafond.effective_at)}
                        {plafond.reference ? <span className="block text-xs text-muted-foreground">{plafond.reference}</span> : null}
                      </td>
                      <td>{libelle('pays', plafond.country)}</td>
                      <td>{nomsProduits[plafond.product_code] || libelle('fuelProduct', plafond.product_code, 'Produit à préciser')}</td>
                      <td className="text-right tabular-nums">{formatMontant(plafond.ceiling_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panneau>
        </div>
      )}

      {station && (
        <PrixDialog
          open={dialogue.type === 'prix'}
          onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null, produitId: null })}
          stationId={station.id}
          lignes={lignes}
          produitInitial={dialogue.produitId}
          onSaved={() => charger(station.id)}
        />
      )}
      <PlafondDialog
        open={dialogue.type === 'plafond'}
        onOpenChange={(ouvert) => !ouvert && setDialogue({ type: null, produitId: null })}
        produits={lignes}
        onSaved={() => charger(stationId)}
      />
    </div>
  );
};

export default StationPricesPage;
